import { useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Trash2, Megaphone, Loader2, ImageOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PosterHealth = ({ items, recordId, token, onChanged, onCreatePoster }) => {
  const [deleting, setDeleting] = useState(null);
  const broken = items.filter((p) => p.status === "pending_fetch");
  const processing = items.filter((p) => p.status === "processing");

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.filename || "this poster"}" from ${p.field}?`)) return;
    setDeleting(p.id);
    try {
      await axios.delete(`${API}/artist/attachment`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { record_id: recordId, attachment_id: p.id, field_type: p.field_type },
      });
      onChanged();
    } catch {
      alert("Could not remove the attachment. Please try again.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <section className="info-card poster-health" data-testid="poster-health">
      <div className="info-card-header">
        <h3 className="info-card-title">AIRTABLE POSTERS</h3>
        <span className={`pill ${broken.length ? "pill-red" : processing.length ? "pill-warn" : items.length ? "pill-green" : "pill-muted"}`} data-testid="poster-health-summary">
          {broken.length ? `${broken.length} BROKEN` : processing.length ? `${processing.length} PROCESSING` : items.length ? "ALL OK" : "NONE"}
        </span>
      </div>

      {broken.length > 0 && (
        <div className="danger-box" data-testid="poster-health-warning">
          <AlertTriangle size={18} />
          <p>
            {broken.length === 1 ? "One poster is" : `${broken.length} posters are`} still waiting for Airtable to download
            {broken.length === 1 ? " it" : " them"} from the portal (older save method). If Airtable can't fetch the file it will remove
            the attachment on its own — if that happens, re-push a fresh poster.
          </p>
        </div>
      )}

      {processing.length > 0 && broken.length === 0 && (
        <p className="adjust-hint" data-testid="poster-health-processing">
          Airtable has the file{processing.length > 1 ? "s" : ""} and is generating previews — this usually takes a few seconds.
        </p>
      )}

      {items.length === 0 ? (
        <p className="field-empty">No posters in Airtable yet.</p>
      ) : (
        <div className="poster-grid">
          {items.map((p) => (
            <div key={p.id} className={`poster-tile ${p.status === "pending_fetch" ? "is-broken" : ""}`} data-testid={`poster-tile-${p.id}`}>
              <div className="poster-tile-img">
                {p.status === "ok" && p.thumb ? <img src={p.thumb} alt={p.filename} /> : p.status === "processing" ? <Loader2 size={28} className="spin" /> : <ImageOff size={28} />}
                <span className={`poster-tile-badge ${p.status === "ok" ? "ok" : p.status === "processing" ? "warn" : "bad"}`}>
                  {p.status === "ok" ? <CheckCircle2 size={12} /> : p.status === "processing" ? <Loader2 size={12} className="spin" /> : <AlertTriangle size={12} />}
                  {" "}{p.status === "ok" ? "OK" : p.status === "processing" ? "PROCESSING" : "WAITING FOR AIRTABLE"}
                </span>
              </div>
              <div className="poster-tile-meta">
                <span className="poster-tile-field">{p.field.toUpperCase()}</span>
                <span className="poster-tile-name" title={p.filename}>{p.filename || "untitled"}</span>
                {p.width && <span className="poster-tile-dim">{p.width} × {p.height}</span>}
              </div>
              <button className="poster-tile-remove" onClick={() => remove(p)} disabled={deleting === p.id} data-testid={`poster-remove-${p.id}`}>
                {deleting === p.id ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} REMOVE
              </button>
            </div>
          ))}
        </div>
      )}

      <button className="btn-announce" onClick={onCreatePoster} style={{ marginTop: 18 }} data-testid="poster-health-create-btn">
        <Megaphone size={15} /> {broken.length ? "RE-PUSH A FRESH POSTER" : "CREATE POSTER"}
      </button>
    </section>
  );
};
